import type { ITenantPlayerBranding } from '@lucimark/shared/models/player-registration-payload'
import lockupDarkAnimatedUrl from '@lucimark/shared-assets/img/lucimark-lockup-dark-animated.svg?url'
import lockupDarkUrl from '@lucimark/shared-assets/img/lucimark-lockup-dark.svg?url'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import usePlayer from '@/composables/usePlayer'
import {
  hasCustomLogoUrn,
  readPlayerBrandingManifest,
  resolvePlayerBrandingUrls
} from '@/helpers/playerBrandingCache'

const prefersReducedMotion
  = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Animated lockup (mark + wordmark) on idle chrome; static when reduced motion. */
const DEFAULT_LOGO_URL = prefersReducedMotion ? lockupDarkUrl : lockupDarkAnimatedUrl

function revokeIfBlobUrl(url: string | null) {
  if (url != null && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function brandingFromManifest(): ITenantPlayerBranding | undefined {
  const manifest = readPlayerBrandingManifest()
  if (manifest == null) {
    return undefined
  }
  const logoUrn = manifest.logoUrn
  const backgroundUrn = manifest.backgroundUrn
  if ((logoUrn == null || logoUrn === '') && (backgroundUrn == null || backgroundUrn === '')) {
    return undefined
  }
  return {
    ...(logoUrn != null && logoUrn !== '' ? { logoUrn } : {}),
    ...(backgroundUrn != null && backgroundUrn !== '' ? { backgroundUrn } : {})
  }
}

function expectCustomLogo(branding: ITenantPlayerBranding | undefined): boolean {
  return hasCustomLogoUrn(branding) || hasCustomLogoUrn(readPlayerBrandingManifest())
}

export function usePlayerBranding() {
  const { state, getApiClientService } = usePlayer()
  const logoUrl = ref(
    expectCustomLogo(state.tenant?.playerBranding)
      ? ''
      : DEFAULT_LOGO_URL
  )
  const backgroundUrl = ref<string | null>(null)

  const logoAlt = computed(() => state.tenant?.name?.trim() || 'Lucimark')
  const useDefaultChrome = computed(() => backgroundUrl.value == null)
  const backgroundLayerStyle = computed(() => {
    if (backgroundUrl.value == null) {
      return undefined
    }
    return {
      backgroundImage: `url(${backgroundUrl.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    } as const
  })

  let syncGeneration = 0

  async function applyResolved(resolved: { logoUrl: string, backgroundUrl: string | null }) {
    if (logoUrl.value !== DEFAULT_LOGO_URL && logoUrl.value !== '') {
      revokeIfBlobUrl(logoUrl.value)
    }
    revokeIfBlobUrl(backgroundUrl.value)
    logoUrl.value = resolved.logoUrl
    backgroundUrl.value = resolved.backgroundUrl
  }

  async function syncBranding() {
    const generation = ++syncGeneration
    const api = state.tenant != null ? getApiClientService() : null
    const branding = state.tenant?.playerBranding ?? (api == null ? brandingFromManifest() : undefined)
    if (expectCustomLogo(branding) && logoUrl.value === DEFAULT_LOGO_URL) {
      // Tenant branding just arrived — drop Lucimark before the async blob resolve.
      logoUrl.value = ''
    }
    let resolved: { logoUrl: string, backgroundUrl: string | null }
    try {
      resolved = await resolvePlayerBrandingUrls({
        branding,
        api,
        defaultLogoUrl: DEFAULT_LOGO_URL
      })
    }
    catch {
      // IndexedDB / network can throw; never leave idle chrome with an empty logo deferral.
      if (generation !== syncGeneration) {
        return
      }
      await applyResolved({ logoUrl: DEFAULT_LOGO_URL, backgroundUrl: null })
      return
    }
    if (generation !== syncGeneration) {
      revokeIfBlobUrl(resolved.logoUrl === DEFAULT_LOGO_URL ? null : resolved.logoUrl)
      revokeIfBlobUrl(resolved.backgroundUrl)
      return
    }
    await applyResolved(resolved)
  }

  watch(
    () => [state.tenant?.id, state.tenant?.playerBranding] as const,
    () => {
      void syncBranding()
    },
    { deep: true, immediate: true }
  )

  onBeforeUnmount(() => {
    syncGeneration++
    if (logoUrl.value !== DEFAULT_LOGO_URL && logoUrl.value !== '') {
      revokeIfBlobUrl(logoUrl.value)
    }
    revokeIfBlobUrl(backgroundUrl.value)
  })

  const hasCustomLogo = computed(() => logoUrl.value !== DEFAULT_LOGO_URL && logoUrl.value !== '')

  return {
    logoUrl,
    logoAlt,
    hasCustomLogo,
    useDefaultChrome,
    backgroundLayerStyle
  }
}
